import { useQuery } from '@tanstack/react-query';
import { Gamepad2, BarChart2, Sparkles, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

type HeroStats = {
  totalGames: number;
  totalTranslations: number;
};

type LatestSupporter = {
  id: number;
  user_name: string;
  user_email: string;
  tier_title: string;
};

type ServerCost = {
  id: number;
  year: number;
  progress_percentage: number;
  is_active: boolean;
};

const fetchHeroStats = async (): Promise<HeroStats> => {
  const response = await fetch('/api/statistics/hero');
  if (!response.ok) {
    throw new Error('A statisztikák betöltése sikertelen.');
  }
  return response.json();
};

const fetchLatestSupporter = async (): Promise<LatestSupporter | null> => {
  const response = await fetch('/api/supporters/active');
  if (!response.ok) {
    throw new Error('A támogatók betöltése sikertelen.');
  }
  const supporters = await response.json();
  return supporters.length > 0 ? supporters[0] : null;
};

const fetchServerCosts = async (): Promise<ServerCost[]> => {
  const response = await fetch('/api/server_costs');
  if (!response.ok) {
    throw new Error('A szerver költségek betöltése sikertelen.');
  }
  return response.json();
};

const HeroSection = () => {
  const { data, isLoading } = useQuery<HeroStats>({
    queryKey: ['heroStats'],
    queryFn: fetchHeroStats,
    staleTime: 1000 * 60 * 5, // 5 percig frissnek tekintjük az adatot
  });
  const { data: latestSupporter } = useQuery<LatestSupporter | null>({
    queryKey: ['latestSupporter'],
    queryFn: fetchLatestSupporter,
    staleTime: 1000 * 60 * 5,
  });
  const { data: serverCosts = [] } = useQuery<ServerCost[]>({
    queryKey: ['serverCosts'],
    queryFn: fetchServerCosts,
    staleTime: 1000 * 60 * 5,
  });
  const { isAdmin, isTranslator } = useAuth();

  // Ellenőrizzük, van-e aktív kampány (progress_percentage < 100 és is_active = true)
  const hasActiveCampaign = serverCosts.some(cost => cost.progress_percentage < 100 && cost.is_active);
  // Ellenőrizzük, van-e bármilyen kampány
  const hasCampaign = serverCosts.length > 0;

  return (
    <section className="text-center py-2 ">
      <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 text-primary">
        Játékfordítások
      </h1>
      <p className="text-lg md:text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
        Fedezd fel a legújabb játékokat magyar nyelven!
        <br />
        A legnagyobb magyar gépi fordítást készítő közösség.
      </p>
      <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mt-8">
        <div className="flex items-center gap-2 text-lg text-foreground">
          <Gamepad2 size={24} className="text-purple-500" />
          <span>{isLoading ? '...' : `${data?.totalGames.toLocaleString() ?? 'N/A'} játék`}</span>
        </div>
        <div className="flex items-center gap-2 text-lg text-foreground">
          <BarChart2 size={24} className="text-blue-500" />
          <span>{isLoading ? '...' : `${data?.totalTranslations.toLocaleString() ?? 'N/A'} fordítás`}</span>
        </div>
        <div className="flex items-center gap-2 text-lg text-foreground">
          <Sparkles size={24} className="text-red-500" />
          <span>Ingyenes letöltés</span>
        </div>
      </div>
      {!isTranslator && hasCampaign && (
        <div className="mt-8 -mx-4 px-4 py-6 bg-orange-500 flex items-start justify-center gap-3">
          <div className="flex items-start justify-center gap-3 max-w-2xl w-full">
            <AlertCircle size={24} className="text-white flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white font-semibold text-sm sm:text-base">
                Az oldal támogatása: {' '}
                <Link to="/ad-free" className="underline hover:text-orange-100 transition-colors">
                  Segítség a szerverek éves költségeinek finanszírozásában.
                </Link>
              </p>              {latestSupporter && (
                <p className="text-white text-sm mt-2">
                  ❤️ Köszönjük <span className="font-semibold">{latestSupporter.user_name}</span> ({latestSupporter.user_email})<br />({latestSupporter.tier_title}) a támogatást!
                </p>
              )}            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;